import {emitGuideCommand} from "@khan/events-guide";
import {
    coeditingEditStreamChannel,
    coeditingEventStreamChannel,
} from "@khan/guide-coeditor-core";
import {RequestInfo} from "@khan/shared-core";

export type CoeditingEvent =
    | {event: "template-structure"; chunks: string[]}
    | {event: "placeholder-completion-started"; placeholderKey: string}
    | {event: "placeholder-completion-finished"; placeholderKey: string}
    | {event: "edit-started"}
    | {event: "edit-finished"}
    | {event: "initial-completion-finished"}
    | {event: "undo"}
    | {event: "set-title"; newTitle: string}
    | {event: "set-read-only"; value: boolean}
    | {
          event: "selection-changed";
          newPrefix: string;
          newSelection: string;
          newSuffix: string;
      };

/**
 * Emits an event on the coediting/event command-stream channel.
 * Normally these events are streamed from the backend, but there
 * may be cases where we want to manually emit these events from
 * the frontend as well.
 *
 * See `useCoeditingEventStream` for how these events get handled.
 */
export const emitCoeditingEvent = (event: CoeditingEvent) => {
    emitGuideCommand({
        name: coeditingEventStreamChannel,
        content: JSON.stringify(event),
    });
};

/**
 * Emits an edit on the coediting/edit command-stream channel.
 * Normally these edits are streamed from the backend, but there
 * may be cases where we want to manually emit these events from
 * the frontend as well.
 *
 * The text given here will be inserted into the open coeditor.
 * See `useCoeditingEditStream` for how these edits get handled.
 */
export const emitCoeditingEdit = (text: string) => {
    emitGuideCommand({
        name: coeditingEditStreamChannel,
        content: JSON.stringify({edit: text}),
    });
};

/**
 * Parses a raw event command received on `coeditingEventStreamChannel` into
 * a `CoeditingEvent`.
 */
export const parseCoeditingEvent = (
    rawEventContent: string,
): CoeditingEvent | void => {
    let data: CoeditingEvent | void = undefined;
    try {
        data = JSON.parse(rawEventContent) as CoeditingEvent;
    } catch (err) {
        if (RequestInfo.IS_DEV_SERVER) {
            throw err;
        }
    }

    return data;
};

/**
 * Parses a raw edit command received on `coeditingEditStreamChannel` into
 * a string (the token or bit of text being inserted as an edit).
 */
export const parseCoeditingEdit = (rawEdit: string): [string, string] => {
    let data = {};
    try {
        data = JSON.parse(rawEdit);
    } catch (err) {
        if (RequestInfo.IS_DEV_SERVER) {
            throw err;
        }
    }

    return [data["placeholderKey"] || "", data["edit"] || ""];
};
